import { createId } from '@paralleldrive/cuid2';
import React from 'react';
import styled from 'styled-components';

import { Configuration } from '@/types/Configuration';

import { AddButton } from '../AddButton/AddButton';
import { Config, ConfigMap } from '../AdminEditPage';
import { SortBy, TableColumnsKeys } from './ColumnHeading/ColumnHeading';
import { PlayerColumn } from './PlayerColum/PlayerColumn';
import { ShelfColumn } from './ShelfColumn/ShelfColumn';
import { StatusTag } from './StatusTag/StatusTag';
import { TableCell } from './TableCell/TableCell';
import { TableHeader } from './TableHeader/TableHeader';

const StyledTableWrapper = styled.table`
  background-color: ${({ theme }) => theme.color('#F6F6F6')};
  border-radius: 17px;
  padding: 32px 32px 32px 32px;
  margin-top: 32px;
  width: 100%;
  flex-grow: 1;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th {
    text-align: left;
    color: ${({ theme }) => theme.color('#676570')};
    font-weight: 400;
    width: 30%;

    &:nth-child(4) {
      width: 10%;
    }
  }
`;

const StyledColumn = styled.td`
  vertical-align: top;
  padding-top: 40px;
  padding-bottom: 32px;
  border-bottom: 1px solid #b2b1ba;
`;

type Props = {
  readonly customer: Configuration | undefined;
  readonly configState: ConfigMap;
  readonly setConfigState: React.Dispatch<React.SetStateAction<ConfigMap>>;
  readonly setSortBy: React.Dispatch<React.SetStateAction<SortBy>>;
  readonly sortBy: SortBy;
};

export const EditTable = ({
  customer,
  configState,
  setConfigState,
  setSortBy,
  sortBy,
}: Props) => {
  const handleColumnSort = (a: Config, b: Config) => {
    const directionFactor = sortBy.direction === 'ASC' ? 1 : -1;

    const shelvesA = a.shelves?.filter((shelf) => !shelf.archived);
    const shelvesB = b.shelves?.filter((shelf) => !shelf.archived);

    switch (sortBy.key) {
      case TableColumnsKeys.SUBSIDIARY:
        return directionFactor * (a.name < b.name ? 1 : -1);

      case TableColumnsKeys.SHELF:
        return directionFactor * (shelvesA.length < shelvesB.length ? 1 : -1);

      case TableColumnsKeys.PLAYER:
        return (
          directionFactor *
          (shelvesA.flatMap(({ players }) => players).length <
          shelvesB.flatMap(({ players }) => players).length
            ? 1
            : -1)
        );

      case TableColumnsKeys.STATUS:
        return (
          directionFactor *
          (a.playerTotals.online / a.playerTotals.total <
          b.playerTotals.online / b.playerTotals.total
            ? 1
            : -1)
        );

      case TableColumnsKeys.COMPANY:
      default:
        return directionFactor * (a.companyName < b.companyName ? 1 : -1);
    }
  };

  const subsidiaries = [...(configState ?? [])].sort(handleColumnSort);

  const saveConfig = (
    configId: string | null,
    newData: Partial<Config> | null,
  ) => {
    setConfigState((prev) => {
      if (!prev || (configId === null && newData === null)) {
        return prev;
      }

      if (configId === null) {
        return [
          ...prev,
          {
            name: '',
            playerTotals: {
              online: 0,
              total: 0,
            },
            shelves: [],
            ...newData,
            companyName: customer?.name ?? '',
            id: createId(),
            unitId: customer?.id ?? '',
          },
        ];
      }

      if (newData === null) {
        return prev.filter(({ id }) => id !== configId);
      }

      return prev.map((config) =>
        config.id === configId
          ? {
              ...config,
              ...newData,
              id: config.id,
              companyName: config.companyName,
              unitId: config.unitId,
            }
          : config,
      );
    });
  };

  return (
    <StyledTableWrapper data-testid="edit-table-wrapper">
      <TableHeader setSortBy={setSortBy} sortBy={sortBy} />
      <tbody>
        {subsidiaries.map(({ id, name, playerTotals, shelves, unitId }) => (
          <tr key={id}>
            <StyledColumn>
              <TableCell
                columnName="Subsidiary"
                item={name}
                onDelete={() => saveConfig(id, null)}
                onEdit={(subsidiaryName) =>
                  saveConfig(id, { name: subsidiaryName })
                }
              />
            </StyledColumn>
            <StyledColumn>
              <ShelfColumn
                saveShelves={(newShelves) =>
                  saveConfig(id, {
                    shelves: newShelves,
                  })
                }
                shelves={shelves}
                subsidiaryId={id}
                unitId={unitId}
                customerId={customer?.id ?? 'unknown_customer'}
              />
            </StyledColumn>
            <StyledColumn>
              <PlayerColumn
                saveShelfPlayers={(newShelves) =>
                  saveConfig(id, { shelves: newShelves })
                }
                shelves={shelves}
                subsidiaryId={id}
              />
            </StyledColumn>
            <StyledColumn>
              <StatusTag playerTotals={playerTotals} />
            </StyledColumn>
          </tr>
        ))}
        <tr>
          <StyledColumn colSpan={4}>
            <AddButton
              columnName="Subsidiary"
              onSave={(name) => saveConfig(null, { name })}
            />
          </StyledColumn>
        </tr>
      </tbody>
    </StyledTableWrapper>
  );
};
